import { Link } from 'react-router-dom';
import { Star, Package, Box } from 'lucide-react';
import type { ProductWithMedia } from '../../types/products';
import { PRODUCT_TYPE_LABELS } from '../../types/products';
import { formatPrice, truncateCategoryPath } from '../../services/productService';

interface ProductCardProps {
  product: ProductWithMedia;
}

export function ProductCard({ product }: ProductCardProps) {
  const primaryImage = product.media.find((m) => m.type === 'image');

  return (
    <Link
      to={`/products/${product.slug}`}
      className="group bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      {/* Image */}
      <div className="aspect-square bg-gray-50 dark:bg-slate-700 relative overflow-hidden">
        {primaryImage ? (
          <img
            src={primaryImage.url}
            alt={primaryImage.alt || product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-16 h-16 text-gray-300 dark:text-slate-500" />
          </div>
        )}

        {/* Product Type Badge */}
        {product.product_type !== 'single' && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary text-white text-xs font-medium rounded-full">
              <Box className="w-3 h-3" />
              {PRODUCT_TYPE_LABELS[product.product_type]}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Category */}
        {product.category_path && (
          <p className="text-xs text-[#6B6B6B] dark:text-slate-400 mb-1 truncate">
            {truncateCategoryPath(product.category_path, 40)}
          </p>
        )}

        {/* Name */}
        <h3 className="font-semibold text-[#1A1A1A] dark:text-white mb-1 line-clamp-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>

        {/* SKU */}
        <p className="text-xs text-[#6B6B6B] dark:text-slate-400 mb-2">Art.-Nr.: {product.sku}</p>

        {/* Price & Rating Row */}
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-[#1A1A1A] dark:text-white">
            {formatPrice(product.price_gross, product.currency)}
          </span>

          {product.rating_avg !== null && product.rating_count > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="font-medium text-[#1A1A1A] dark:text-white">
                {product.rating_avg.toFixed(1)}
              </span>
              <span className="text-[#6B6B6B] dark:text-slate-400">({product.rating_count})</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
